<template>
  <div class="page-container">
    <div v-if="isLoading" class="loading-state">
      <div class="spinner"></div>
      <p>Menganalisis hasil Anda...</p>
    </div>
    
    <div v-else-if="error" class="error-state">
      <h2>Terjadi Kesalahan</h2>
      <p>{{ error }}</p>
      <button @click="backToDashboard" class="button-secondary">Kembali ke Dashboard</button>
    </div>

    <div v-else-if="report" class="report-content" id="report-to-print">
      <header class="report-header">
        <h1>Laporan Hasil Psikogram</h1>
        <div class="participant-info">
          <div><strong>Nama:</strong> {{ report.participant_name }}</div>
          <div><strong>No. Tes:</strong> {{ report.participant_test_number }}</div>
          <div><strong>Tanggal Tes:</strong> {{ formatDate(report.test_date) }}</div>
        </div>
      </header>

      <div v-if="report.report_status === 'pending'" class="pending-state">
        <h2>Hasil Belum Tersedia</h2>
        <p>{{ report.psychologist_notes || 'Hasil Anda sedang diproses atau Anda belum menyelesaikan semua tes.' }}</p>
        <button @click="backToDashboard" class="button-primary">Kembali ke Dashboard</button>
      </div>

      <div v-if="report.report_status === 'ready'" class="ready-state">
        <div class="summary-card">
          <h2>Kesimpulan Akhir: <span :class="conclusionClass">{{ report.conclusion }}</span></h2>
        </div>

        <div class="details-card">
          <h3>Rincian Aspek Psikologis</h3>
          <div v-for="aspect in report.report_details" :key="aspect.name" class="aspect-group">
            <h4>{{ aspect.name }}</h4>
            <ul>
              <li v-for="sub in aspect.sub_aspects" :key="sub.name">
                <span>{{ sub.name }}</span>
                <span class="category-badge">{{ sub.category }}</span>
              </li>
            </ul>
          </div>
        </div>

        <div v-if="report.psychologist_notes" class="notes-card">
          <h3>Catatan dari Psikolog</h3>
          <p class="notes-text">{{ report.psychologist_notes }}</p>
        </div>

        <div v-if="report.psychologist_name" class="psychologist-info">
          <p><strong>Pemeriksa:</strong> {{ report.psychologist_name }}</p>
          <p v-if="report.psychologist_sipp"><strong>SIPP:</strong> {{ report.psychologist_sipp }}</p>
        </div>
      </div>
    </div>

    <footer class="page-actions" v-if="!isLoading && report && report.report_status === 'ready'">
      <button @click="generatePdf" class="button-primary">Download Laporan PDF</button>
      <button @click="backToDashboard" class="button-secondary">Kembali ke Dashboard</button>
    </footer>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import apiClient from '@/services/api.js';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import qr from 'qrcode';
import logoImage from '@/assets/logo1.png';
import watermarkImage from '@/assets/watermark.png';

const router = useRouter();
const isLoading = ref(true);
const report = ref(null);
const error = ref('');

const conclusionClass = computed(() => {
  if (!report.value?.conclusion) return '';
  return report.value.conclusion.toLowerCase().includes('lulus') ? 'lulus' : 'tidak-lulus';
});

const formatDate = (dateString, dateOnly = true) => {
  if (!dateString) return '-';
  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  if (!dateOnly) {
    options.hour = '2-digit';
    options.minute = '2-digit';
  }
  return new Date(dateString).toLocaleDateString('id-ID', options);
};

onMounted(async () => {
  isLoading.value = true;
  try {
    const response = await apiClient.get('/sessions/my-report');
    report.value = response.data;
  } catch (err) {
    error.value = "Gagal memuat laporan hasil. Sesi Anda mungkin tidak valid.";
    console.error("Report fetch error:", err.response);
  } finally {
    isLoading.value = false;
  }
});

const backToDashboard = () => router.push({ name: 'TestDashboard' });

const generatePdf = async () => {
  if (!report.value || report.value.report_status !== 'ready') {
    alert("Laporan belum siap untuk dicetak.");
    return;
  }

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: [216, 330] });
  let signatureBase64 = null, watermarkBase64 = null, qrCodeBase64 = null;

  try {
    // Load signature image
    if (report.value.psychologist_signature_url) {
      signatureBase64 = await new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/png'));
        };
        img.onerror = reject;

        // --- LOGIKA KUNCI UNTUK DEVELOPMENT & PRODUKSI ---
        const backendUrl = process.env.NODE_ENV === 'production'
            ? window.location.origin
            : 'http://148.230.97.203:8011';

        console.log(`[PDF Generator] NODE_ENV is: ${process.env.NODE_ENV}`);
        console.log(`[PDF Generator] Using backend URL: ${backendUrl}`);

        img.src = `${backendUrl}${report.value.psychologist_signature_url}`;
      });
    }

    // Load watermark image
    const imgWatermark = new Image();
    watermarkBase64 = await new Promise((resolve, reject) => {
      imgWatermark.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = imgWatermark.width;
        canvas.height = imgWatermark.height;
        const ctx = canvas.getContext('2d');
        ctx.globalAlpha = 0.08;
        ctx.drawImage(imgWatermark, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      imgWatermark.onerror = reject;
      imgWatermark.src = watermarkImage;
    });

    // Generate QR Code
    const baseUrl = `${window.location.origin}/verify`;
    const qrData = `${baseUrl}?id=${report.value.participant_test_number}`;
    qrCodeBase64 = await qr.toDataURL(qrData, {
      width: 15, margin: 1, color: { dark: '#000000', light: '#FFFFFF' }
    });

  } catch (err) {
    console.error("Gagal men-generate aset gambar (QR/Watermark/Tanda Tangan):", err);
    alert("Gagal memuat aset gambar untuk PDF. Coba lagi.");
    return;
  }

  const kota = report.value.psychologist_city || 'Kota';
  const namaPsikolog = report.value.psychologist_name || 'Psikolog Pemeriksa';
  const sipp = report.value.psychologist_sipp ? `SIPP: ${report.value.psychologist_sipp}` : '';

  // --- HEADER ---
  doc.addImage(logoImage, 'PNG', 10, 7, 30, 30);
  doc.setFontSize(10).setFont('helvetica', 'bold').text("PT. PRERANA ETOS INPIRASI", 100, 15, { align: 'center' });
  doc.setFont('helvetica', 'normal').text("PRAKTEK PSIKOLOGI", 100, 20, { align: 'center' });
  doc.setFont('helvetica', 'italic').text(namaPsikolog, 100, 25, { align: 'center' });
  doc.text(kota, 100, 30, { align: 'center' });
  doc.addImage(qrCodeBase64, 'PNG', 170, 15, 15, 15);
  doc.setFontSize(6).setTextColor(100, 100, 100).text("Scan", 177.5, 34, { align: 'center' });

  // --- JUDUL UTAMA ---
  doc.setFontSize(12).setFont('helvetica', 'bold').setTextColor(0).text("A. PSIKOGRAM HASIL PEMERIKSAAN PSIKOLOGI PENERBITAN SURAT IZIN MENGEMUDI", 10, 40);

  // --- DATA PESERTA ---
  let yPos = 52;
  const labelX = 10;
  const valueX = 45;

  // Menggunakan field dari report.value
  const dataRowsA = [
    { label: "NIK ", value: report.value.participant_test_number || "-" },
    { label: "Nama", value: report.value.participant_name || "-" },
    { label: "Tanggal Lahir", value: formatDate(report.value.participant_birth_date, true) || "-" },
    { label: "Pekerjaan", value: report.value.participant_job || "-" },
    { label: "Tanggal Tes", value: formatDate(report.value.test_date, true) || "-" },
    { label: "Keperluan SIM", value: `${report.value.sim_type || ''} (${report.value.sim_status || ''})` }, // Tambahkan baris ini
    { label: "Lokasi Tes", value: report.value.test_location || "-" }, // Tambahkan baris ini
    { label: "Alamat", value: report.value.participant_address || "-" } // Diasumsikan field ini ada di report
  ];

  dataRowsA.forEach(row => {
    doc.setFont('helvetica', 'normal'); // Tambahkan ini untuk konsistensi
    doc.text(row.label, labelX, yPos);
    doc.text(":", 40, yPos);
    if (row.label === "Alamat") {
      const lines = doc.splitTextToSize(row.value, 130);
      doc.text(lines, valueX, yPos);
      yPos += lines.length * 5;
    } else {
      doc.text(row.value, valueX, yPos);
    }
    yPos += 5;
  });

  // --- TABEL ASPEK PSIKOLOGIS ---
  const head = [['No.', 'Aspek Psikologis', 'Kurang', 'Cukup', 'Baik']];
  const body = [];
  const conclusion = report.value.conclusion || 'TIDAK LULUS';

  try {
    const reportDetails = report.value.report_details || [];
    let rowCounter = 1;
    reportDetails.forEach(aspect => {
      body.push([
        { content: rowCounter++ + '.', styles: { fontStyle: 'bold' } },
        { content: aspect.name, colSpan: 4, styles: { fontStyle: 'bold' } }
      ]);

      aspect.sub_aspects.forEach(sub => {
        const category = sub.category ? sub.category.toUpperCase() : 'N/A';
        body.push([
          '',
          sub.name,
          category === 'K' || category === 'KURANG' ? 'X' : '',
          category === 'C' || category === 'CUKUP' ? 'X' : '',
          category === 'B' || category === 'BAIK' ? 'X' : ''
        ]);
      });
    });

    body.push(['', { content: 'Kesimpulan: ' + conclusion, colSpan: 5, styles: { fontStyle: 'bold' } }]);
  } catch (e) {
    console.error("Gagal parse report_details:", e);
    body.push(['', { content: 'Gagal memuat data analisis.', colSpan: 5, styles: { fontStyle: 'bold', textColor: [255, 0, 0] } }]);
  }

  if (!Array.isArray(body) || body.length === 0) {
    body.push(['', { content: 'Tidak ada data analisis yang tersedia.', colSpan: 5, styles: { fontStyle: 'italic', textColor: [100, 100, 100] } }]);
  }

  autoTable(doc, {
    head,
    body,
    startY: yPos,
    theme: 'grid',
    margin: { left: 10, right: 10 },
    headStyles: {
      fillColor: [240, 240, 240],
      textColor: [0, 0, 0],
      halign: 'center',
      fontSize: 10,
      fontStyle: 'bold'
    },
    columnStyles: {
      0: { halign: 'center', cellWidth: 15 },
      1: { cellWidth: 90 },
      2: { halign: 'center', cellWidth: 20 },
      3: { halign: 'center', cellWidth: 20 },
      4: { halign: 'center', cellWidth: 20 }
    },
    didParseCell: (data) => {
      if (data.row.index < body.length && data.cell.section === 'head') {
        data.cell.styles.fontSize = 10;
      }
    }
  });

  yPos = doc.lastAutoTable.finalY + 10;

  // --- BAGIAN B: KETERANGAN HASIL PEMERIKSAAN PSIKOLOGI ---
  doc.setFontSize(12).setFont('helvetica', 'bold');
  doc.text("B. KETERANGAN HASIL PEMERIKSAAN PSIKOLOGI", 10, yPos);
  yPos += 5;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.text("Yang bertanda tangan di bawah ini menerangkan bahwa:", 10, yPos);
  yPos += 6;

  const dataRowsB = [
    { label: "NIK", value: report.value.participant_test_number || "-" },
    { label: "Nama", value: report.value.participant_name || "-" },
    { label: "Tanggal Lahir", value: formatDate(report.value.participant_birth_date, true) || "-" },
    { label: "Pekerjaan", value: report.value.participant_job || "-" },
    { label: "Tanggal Tes", value: formatDate(report.value.test_date, true) || "-" },
    { label: "Alamat", value: report.value.participant_address || "-" }
  ];

  dataRowsB.forEach(row => {
    doc.setFont('helvetica', 'normal'); // Tambahkan ini untuk konsistensi
    doc.text(row.label, labelX, yPos);
    doc.text(":", 40, yPos);
    if (row.label === "Alamat") {
      const lines = doc.splitTextToSize(row.value, 130);
      doc.text(lines, valueX, yPos);
      yPos += lines.length * 5;
    } else {
      doc.text(row.value, valueX, yPos);
    }
    yPos += 5;
  });

  // -- yPos += 2; // Hapus atau komentar baris ini jika tidak diperlukan

   // --- NARASI DINAMIS ---
   const tanggalPelaksanaan = formatDate(report.value.test_date, true);
   const simInfo = `${report.value.sim_type || ''}`.trim();
   const statusInfo = `${report.value.sim_status || ''}`.trim();
   const lokasiTes = report.value.test_location || 'Lokasi Tes';
   const conclusionText = conclusion; // Gunakan nilai conclusion dari atas

   // Buat teks narasi lengkap
   const narasiFull = `Telah melaksanakan pemeriksaan psikologi sebagai syarat untuk penerbitan Surat Izin Mengemudi ${simInfo} (${statusInfo}) pada tanggal ${tanggalPelaksanaan} bertempat di ${lokasiTes}, dengan hasil ${conclusionText}.`;

   // Gunakan splitTextToSize untuk memecah teks narasi menjadi baris-baris
   let lines = doc.splitTextToSize(narasiFull, 182);

   // Tulis setiap baris
   let currentY = yPos;
   for (let line of lines) {
     // Cek apakah baris ini mengandung kesimpulan
     if (line.includes(conclusionText)) {
       // Pisahkan teks menjadi bagian sebelum dan sesudah kesimpulan
       const parts = line.split(conclusionText);

       // Tambahkan spasi eksplisit setelah "hasil"
       // Kita akan menulis: [bagian sebelum][spasi][kesimpulan][bagian setelah]
       let x = 10;

       // Tulis bagian sebelum kesimpulan (jika ada) dengan font normal
       if (parts[0]) {
         doc.setFont('helvetica', 'normal');
         doc.text(parts[0], x, currentY);
         x += doc.getTextWidth(parts[0]);
       }

       // Tambahkan spasi sebelum kesimpulan
       const spaceWidth = doc.getTextWidth(' ');
       x += spaceWidth; // Pindahkan posisi X ke kanan sebesar lebar satu spasi

       // Tulis kesimpulan dengan font bold
       doc.setFont('helvetica', 'bold');
       doc.text(conclusionText, x, currentY);
       x += doc.getTextWidth(conclusionText);

       // Tulis bagian setelah kesimpulan (jika ada) dengan font normal
       if (parts[1]) {
         doc.setFont('helvetica', 'normal');
         doc.text(parts[1], x, currentY);
       }
     } else {
       // Jika baris ini tidak mengandung kesimpulan, tulis dengan font normal
       doc.setFont('helvetica', 'normal');
       doc.text(line, 10, currentY);
     }

     currentY += 5; // Pindah ke baris berikutnya
   }

   // Perbarui yPos untuk elemen berikutnya
   yPos = currentY + 5;

  doc.text("Surat keterangan ini agar dipergunakan sebagaimana mestinya.", 10, yPos);
  yPos += 7;

  doc.setFontSize(9);
  doc.text("NB: Surat Keterangan ini berlaku 6 bulan sejak dinyatakan lulus, terhitung sejak tanggal dikeluarkan.", 10, yPos);
  yPos += 5;
  doc.text("*Agar dilengkapi sesuai dengan golongan SIM", 10, yPos);

  // --- TANDA TANGAN ---
  const pageHeight = doc.internal.pageSize.getHeight();
  let signY = pageHeight - 45;

  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(`${kota}, ${new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}`, 155, signY, { align: 'center' });
  signY += 5;
  doc.setFont('helvetica', 'bold');
  doc.text("PSIKOLOG PEMERIKSA,", 155, signY, { align: 'center' });

  if (signatureBase64) {
    signY += 3;
    const sigWidth = 20;
    const sigHeight = 20;
    doc.addImage(signatureBase64, 'PNG', 155 - sigWidth / 2, signY, sigWidth, sigHeight);
    signY += sigHeight;
  } else {
    signY += 20;
  }

  signY += 3;
  doc.setLineWidth(0.5);
  doc.line(130, signY, 180, signY);

  signY += 5;
  doc.setFont('helvetica', 'bold');
  doc.text(namaPsikolog, 155, signY, { align: 'center' });

  signY += 5;
  doc.setFontSize(9);
  doc.text(sipp, 155, signY, { align: 'center' });

  // --- WATERMARK ---
  if (watermarkBase64) {
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.saveGraphicsState();

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      const watermarkWidth = 80;
      const watermarkHeight = 80;

      doc.addImage(
        watermarkBase64,
        'PNG',
        (pageWidth - watermarkWidth) / 2,
        (pageHeight - watermarkHeight) / 2 - 30,
        watermarkWidth,
        watermarkHeight
      );

      doc.restoreGraphicsState();
    }
  }

  // --- SIMPAN PDF ---
  doc.save(`Laporan_${report.value.participant_name}_${report.value.participant_test_number}.pdf`);
};
</script>

<style scoped>
/* GENERAL LAYOUT */
.page-container {
  max-width: 800px;
  margin: 40px auto;
  padding: 20px;
  background-color: #f9fafb;
}

/* STATES (LOADING, ERROR, PENDING) */
.loading-state, .error-state, .pending-state {
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}
.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 15px;
}
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* REPORT CONTENT */
.report-content {
  background-color: white;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}
.report-header {
  text-align: center;
  border-bottom: 2px solid #eee;
  padding-bottom: 20px;
  margin-bottom: 30px;
}
.report-header h1 {
  margin: 0;
  font-size: 2em;
}
.participant-info {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 15px;
  color: #555;
  flex-wrap: wrap;
}

/* CARDS */
.summary-card, .details-card, .notes-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 25px;
}
.summary-card {
  text-align: center;
  background-color: #f8f9fa;
}
.summary-card h2 {
  margin: 0;
  font-size: 1.5em;
}
.summary-card .lulus { color: #28a745; }
.summary-card .tidak-lulus { color: #dc3545; }

.details-card h3, .notes-card h3 {
  margin-top: 0;
  border-bottom: 1px solid #eee;
  padding-bottom: 10px;
  margin-bottom: 15px;
}

/* DETAILS LIST */
.aspect-group { margin-bottom: 15px; }
.aspect-group h4 { margin: 0 0 10px 0; color: #333; }
.aspect-group ul { list-style: none; padding-left: 15px; margin: 0; }
.aspect-group li {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f5f5f5;
}
.category-badge {
  font-weight: bold;
  padding: 3px 8px;
  border-radius: 4px;
  background-color: #e9ecef;
  color: #495057;
  font-size: 0.9em;
}

/* NOTES & PSYCHOLOGIST INFO */
.notes-text {
  white-space: pre-wrap;
  line-height: 1.6;
}
.psychologist-info {
  margin-top: 30px;
  text-align: right;
  font-size: 0.9em;
  color: #666;
}
.psychologist-info p { margin: 2px 0; }

/* ACTIONS FOOTER */
.page-actions {
  margin-top: 30px;
  text-align: center;
  display: flex;
  justify-content: center;
  gap: 15px;
}
.button-primary, .button-secondary {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1em;
  text-decoration: none;
  display: inline-block;
  transition: background-color 0.2s;
}
.button-primary {
  background-color: #007bff;
  color: white;
}
.button-primary:hover {
  background-color: #0056b3;
}
.button-secondary {
  background-color: #6c757d;
  color: white;
}
.button-secondary:hover {
  background-color: #545b62;
}

/* PRINT STYLES */
@media print {
  body, .page-container {
    background-color: white;
    margin: 0;
    padding: 0;
    box-shadow: none;
  }
  .page-actions {
    display: none;
  }
  .report-content {
    box-shadow: none;
    border: none;
  }
}
</style>